import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { IPpcComplianceFilter } from '../../model/types/PpcComplianceFilter.interface'
import {
  selectPpcComplianceCounter,
  selectPpcComplianceFilter,
  selectProject,
  setPpcComplianceFilter,
  setPpcComplianceStatus,
  setProject,
} from '../../model/ppcComplianceSlice'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { Controller, useForm } from 'react-hook-form'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import {
  BpSearchInput,
  IOption,
} from '../../../../shared/components/forms/search-input/BpSearchInput'
import { FilterDrop } from '../../../../shared/components/cards/filter-drop/FilterDrop'
import moment from 'moment'
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_RANGE_FORMAT,
} from '../../../../shared/consts/DefaultRangeFormat.const'
import React, { useEffect, useMemo } from 'react'
import { BpTabs } from '../../../../shared/components/utility/tabs/BpTabs'
import { BpTab } from '../../../../shared/components/utility/tabs/tab/BpTab'
import { PpcCompliancePanel } from '../ppc-compliance-panel/PpcCompliancePanel'
import { Switch } from 'antd'
import { Trans, useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import { AddProjectButton } from '../../../../shared/components/buttons/add-project-button/AddProjectButton'
import { EditCountriesButton } from '../../../../shared/components/buttons/edit-countries-button/EditCountriesButton'
import { selectNotifications } from '../../../../app/store/reducers/AuthSlice'
import { FilterChips } from '../../../../shared/components/utility/filter-chips/FilterChips'
import { DateSelect } from '../../../../shared/components/forms/date-select/DateSelect'
import { useSearchState } from '../../../../lib/hooks/useSearchState'
import { useReversedFilters } from '../../../../lib/hooks/useReversedFilters'
import {
  resetFilterFields,
  useRegisterAppliedFilters,
} from '../../../../lib/context/applied-filters/AppliedFiltersProvider'
import clsx from 'clsx'
import styles from './PpcComplianceFilters.module.scss'
import { Notify } from '../../../../shared/components/text/notify/Notify'
import {
  GetFilterAdvertiserDomainsApiArg,
  GetFilterAffiliatesApiArg,
  GetFilterCityApiArg,
  GetFilterKeywordsApiArg,
  useGetFilterAdvertiserDomainsQuery,
  useGetFilterAdvertiserNamesQuery,
  useGetFilterAffiliatesQuery,
  useGetFilterAffiliateStatusQuery,
  useGetFilterBrowserQuery,
  useGetFilterCityQuery,
  useGetFilterCountryQuery,
  useGetFilterEngineQuery,
  useGetFilterKeywordsQuery,
  useGetFilterProjectsQuery,
} from '../../../../app/api'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import { ProjectIcon } from '../../../../shared/components/icons/filters/ProjectIcon'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { SearchEngineIcon } from '../../../../shared/components/icons/filters/SearchEngineIcon'
import { BrowserIcon } from '../../../../shared/components/icons/filters/BrowserIcon'
import { CityIcon } from '../../../../shared/components/icons/filters/CityIcon'
import { KeywordIcon } from '../../../../shared/components/icons/filters/KeywordIcon'
import { AdvertiserIcon } from '../../../../shared/components/icons/filters/AdvertiserIcon'
import { DomainIcon } from '../../../../shared/components/icons/filters/DomainIcon'
import { AffiliateIcon } from '../../../../shared/components/icons/filters/AffiliateIcon'
import { withSelectedOptions } from '../../../../shared/lib/helpers/WithSelectedOptions'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'
import { restoreAppliedValues } from '../../../../shared/lib/helpers/AppliedValues'

const REVERSED_FILTER_KEYS: Record<string, string> = {
  network: 'network',
  site: 'site',
  advertiserName: 'advertiser_id',
  engine: 'engine_id',
  browser: 'browser_id',
  city: 'city_id',
  keyword: 'keyword',
}

/** Фильтры, которые сбрасывает Clear filters, и их значения по умолчанию. */
const CLEARABLE_FILTERS: Partial<IPpcComplianceFilter> = {
  country: '',
  affiliate: '',
  network: '',
  site: '',
  engine: '',
  browser: '',
  city: '',
  advertiserName: '',
  keyword: '',
  hidden: false,
}

const Filter = ({
  searchParams,
  setSearchParams,
}: IWithSearchParamsProps<IPpcComplianceFilter>) => {
  const { t } = useTranslation()
  const location = useLocation()
  const filters = useAppSelector<IPpcComplianceFilter>(selectPpcComplianceFilter)
  const {
    project,
    country,
    affiliate,
    network,
    dateRange,
    site,
    engine,
    browser,
    city,
    hidden,
    status,
    shoppingAds,
    keyword,
    advertiserName,
    reversed_filters,
  } = filters
  const counter = useAppSelector(selectPpcComplianceCounter)
  const { is_demo: isDemo, has_paid_links: hasPaidLinks } = useAppSelector(selectProject)
  const notifications = useAppSelector(selectNotifications)
  const dispatch = useAppDispatch()
  const [search, setSearch] = useSearchState({ keyword, site, advertiserName: '', affiliate: '' })

  const showTrackingNotification = useMemo(() => {
    return notifications?.some(
      (value) =>
        String(value.project_id) === project && value.type === 'affiliate_tracking_disabled',
    )
  }, [notifications, project])

  const {
    getValues,
    setValue,
    handleSubmit,
    control,
    formState: { defaultValues },
  } = useForm<IPpcComplianceFilter>({
    mode: 'onChange',
    defaultValues: {
      project,
      country,
      affiliate,
      network,
      dateRange,
      site,
      keyword,
      advertiserName,
      engine,
      browser,
      city,
      hidden,
      ...searchParams,
    },
  })

  useEffect(() => {
    applyFilter()
  }, [])

  useEffect(() => {
    if (!location.search) {
      setValue('affiliateRule', '')
      applyFilter()
    }
  }, [location.search])

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetFilterProjectsQuery({})

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetFilterCountryQuery(
    {
      projectId: project ? +project : undefined,
    },
    {
      skip: !project,
    },
  )

  const {
    data: affiliates,
    isFetching: affiliatesFetching,
    isLoading: affiliatesLoading,
  } = useGetFilterAffiliatesQuery(
    {
      date: dateRange,
      isSeo: false,
      projectId: +project,
      search: search.affiliate,
    } as GetFilterAffiliatesApiArg,
    { skip: !project },
  )

  const {
    data: domains,
    isFetching: domainsFetching,
    isLoading: domainsLoading,
  } = useGetFilterAdvertiserDomainsQuery(
    {
      projectId: project ? +project : undefined,
      search: search.site,
      date: dateRange,
      seo: false,
    } as GetFilterAdvertiserDomainsApiArg,
    { skip: !project },
  )

  const {
    data: keywords,
    isFetching: keywordsFetching,
    isLoading: keywordsLoading,
  } = useGetFilterKeywordsQuery(
    {
      projectId: project,
      search: search.keyword,
    } as unknown as GetFilterKeywordsApiArg,
    { skip: !project },
  )

  const {
    data: advertiserNames,
    isFetching: advertiserNamesFetching,
    isLoading: advertiserNamesLoading,
  } = useGetFilterAdvertiserNamesQuery(
    {
      date: dateRange,
      projectId: +project,
      search: search.advertiserName,
    },
    { skip: !project },
  )

  const {
    data: engines,
    isFetching: enginesFetching,
    isLoading: enginesLoading,
  } = useGetFilterEngineQuery({ projectId: project ? +project : undefined }, { skip: !project })

  const {
    data: browsers,
    isFetching: browsersFetching,
    isLoading: browsersLoading,
  } = useGetFilterBrowserQuery()

  const {
    data: cities,
    isFetching: citiesFetching,
    isLoading: citiesLoading,
  } = useGetFilterCityQuery(
    {
      projectId: project ? +project : undefined,
      countryId: country || undefined,
    } as unknown as GetFilterCityApiArg,
    { skip: !project },
  )

  const { data: statuses } = useGetFilterAffiliateStatusQuery({ all: true })

  const notify = isDemo
    ? 'demo-project'
    : showTrackingNotification
      ? 'tracking-disabled'
      : undefined

  const applyFilterWithReversed = (reversed: string[]): void => {
    const values = { ...getValues(), reversed_filters: reversed.join(',') }
    dispatch(setPpcComplianceFilter({ ...values, shoppingAds }))
    if (setSearchParams) setSearchParams(values)
  }

  const {
    reversedFilters,
    isReversed,
    isReversedApplied,
    toggleReversedFilter,
    toggleReversedFilterAndApply,
    clearReversedFilter,
    resetReversedFilters,
    restoreReversedFilters,
  } = useReversedFilters({
    keys: REVERSED_FILTER_KEYS,
    applied: reversed_filters,
    fromUrl: searchParams?.reversed_filters,
    onApply: applyFilterWithReversed,
  })

  const applyFilter = (): void => applyFilterWithReversed(reversedFilters)

  useRegisterAppliedFilters({
    values: filters,
    defaults: CLEARABLE_FILTERS,
    onClear: () => {
      resetFilterFields(setValue, CLEARABLE_FILTERS)
      resetReversedFilters()
      applyFilterWithReversed([])
    },
  })

  return (
    <section>
      <form className={clsx(styles.form)} onSubmit={handleSubmit(applyFilter)}>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue =
                defaultValues?.project ||
                project ||
                projects?.find((option) => !option.is_demo)?.value ||
                projects?.[0]?.value

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={ProjectIcon}
                  bottomSection={<AddProjectButton />}
                  defaultValue={defaultValue}
                  disabled={projectsLoading || projectsFetching}
                  fieldSize='lg'
                  loading={projectsLoading || projectsFetching}
                  options={projects}
                  title={t('fields.project')}
                  onChange={(e) => {
                    onChange(e)

                    const option = projects?.find((option) => option.value === e.target.value)

                    if (option) {
                      dispatch(setProject(option))
                    }

                    if (e.target.value !== defaultValue) {
                      setValue('country', '')
                    }

                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='project'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => {
              return (
                <BpSelect
                  {...fieldProps}
                  multiple
                  Icon={CountryIcon}
                  bottomSection={<EditCountriesButton project={{ value: project, isDemo }} />}
                  disabled={countriesLoading || countriesFetching}
                  fieldSize='lg'
                  loading={countriesLoading || countriesFetching}
                  optionRender={renderCountryOption}
                  options={countries}
                  title={t('fields.country')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onDropClose={applyFilter}
                ></BpSelect>
              )
            }}
            control={control}
            name='country'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                multiple
                Icon={AffiliateIcon}
                enableChipLoad={false}
                fieldSize='lg'
                loading={affiliatesLoading || affiliatesFetching}
                options={withSelectedOptions('affiliate', affiliates, value)}
                placeholder={t('fields.affiliate')}
                value={value?.split(',')}
                onChange={(e) => {
                  onChange(
                    [].slice
                      .call(e.target.selectedOptions)
                      .map((o: IOption) => o.value)
                      .join(','),
                  )
                }}
                onDropClose={applyFilter}
                onSearch={setSearch('affiliate')}
              ></BpSelect>
            )}
            control={control}
            name='affiliate'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, value } }) => {
              return (
                <DateSelect
                  value={[moment(value, DEFAULT_DATE_FORMAT), moment(value, DEFAULT_RANGE_FORMAT)]}
                  onChange={(_, value) => {
                    onChange(`${value[0]} - ${value[1]}`)
                    applyFilter()
                  }}
                />
              )
            }}
            control={control}
            name='dateRange'
          />
        </div>
        <div className={clsx(styles.filterDropWrapper)}>
          <FilterDrop
            values={getValues([
              'network',
              'site',
              'engine',
              'browser',
              'city',
              'hidden',
              'keyword',
              'advertiserName',
            ])}
            onClear={() => {
              setValue('network', '')
              setValue('site', '')
              setValue('engine', '')
              setValue('browser', '')
              setValue('city', '')
              setValue('advertiserName', '')
              setValue('keyword', '')
              resetReversedFilters()
            }}
            onHide={() => {
              restoreAppliedValues(getValues, setValue, filters)
              restoreReversedFilters()
            }}
            onSubmit={applyFilter}
          >
            <Controller
              render={({ field }) => (
                <BpSearchInput
                  {...field}
                  action={
                    <BpCheckbox
                      checked={isReversed('network')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('network')}
                    />
                  }
                  label={t('fields.network')}
                  placeholder={t('fields.other.search')}
                ></BpSearchInput>
              )}
              control={control}
              name='network'
            />
            <Controller
              render={({ field }) => (
                <BpSearchInput
                  {...field}
                  action={
                    <BpCheckbox
                      checked={isReversed('site')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('site')}
                    />
                  }
                  Icon={DomainIcon}
                  label={t('fields.domain')}
                  loading={domainsLoading || domainsFetching}
                  options={domains}
                  placeholder={t('fields.other.search')}
                  onSearch={setSearch('site')}
                ></BpSearchInput>
              )}
              control={control}
              name='site'
            />
            <Controller
              render={({ field }) => (
                <BpSearchInput
                  {...field}
                  action={
                    <BpCheckbox
                      checked={isReversed('keyword')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('keyword')}
                    />
                  }
                  Icon={KeywordIcon}
                  label={t('fields.keyword')}
                  loading={keywordsLoading || keywordsFetching}
                  options={keywords}
                  placeholder={t('fields.other.search')}
                  onSearch={setSearch('keyword')}
                ></BpSearchInput>
              )}
              control={control}
              name='keyword'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('advertiserName')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('advertiserName')}
                    />
                  }
                  Icon={AdvertiserIcon}
                  enableChipLoad={false}
                  label={t('fields.advertiser')}
                  loading={advertiserNamesLoading || advertiserNamesFetching}
                  options={withSelectedOptions('advertiser', advertiserNames, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setSearch('advertiserName')}
                ></BpSelect>
              )}
              control={control}
              name='advertiserName'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('engine')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('engine')}
                    />
                  }
                  Icon={SearchEngineIcon}
                  disabled={enginesLoading || enginesFetching}
                  label={t('fields.searchEngine')}
                  loading={enginesLoading || enginesFetching}
                  options={engines}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                />
              )}
              control={control}
              name='engine'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('browser')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('browser')}
                    />
                  }
                  Icon={BrowserIcon}
                  disabled={browsersLoading || browsersFetching}
                  label={t('fields.browser')}
                  loading={browsersLoading || browsersFetching}
                  options={browsers}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                />
              )}
              control={control}
              name='browser'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('city')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('city')}
                    />
                  }
                  Icon={CityIcon}
                  disabled={citiesLoading || citiesFetching}
                  label={t('fields.city')}
                  loading={citiesLoading || citiesFetching}
                  options={cities}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                />
              )}
              control={control}
              name='city'
            />
            <Controller
              render={({ field }) => (
                <div className={clsx(styles.switchRow)}>
                  <span className={clsx(styles.switchLabel)}>
                    {t('fields.showHiddenAffiliates')}
                  </span>
                  <Switch {...field} defaultChecked={defaultValues?.hidden} />
                </div>
              )}
              control={control}
              name='hidden'
            />
          </FilterDrop>
        </div>
      </form>
      <div className={clsx(styles.chipsWrapper)}>
        <FilterChips
          applyCallBack={() => {
            resetReversedFilters()
            applyFilterWithReversed([])
          }}
          clearCallBack={(field: any) => {
            setValue(field, '')
            applyFilterWithReversed(clearReversedFilter(field))
          }}
          items={[
            {
              title: t('fields.network'),
              field: 'network',
              value: network,
              reversible: true,
              reversed: isReversedApplied('network'),
            },
            {
              title: t('fields.domain'),
              field: 'site',
              value: site,
              reversible: true,
              reversed: isReversedApplied('site'),
            },
            {
              title: t('fields.searchEngine'),
              field: 'engine',
              value: engine,
              data: engines,
              type: 'multiselect',
              reversible: true,
              reversed: isReversedApplied('engine'),
            },
            {
              title: t('fields.browser'),
              field: 'browser',
              value: browser,
              data: browsers,
              type: 'multiselect',
              reversible: true,
              reversed: isReversedApplied('browser'),
            },
            {
              title: t('fields.city'),
              field: 'city',
              value: city,
              data: cities,
              type: 'multiselect',
              reversible: true,
              reversed: isReversedApplied('city'),
            },
            {
              title: t('fields.advertiser'),
              field: 'advertiserName',
              value: advertiserName,
              reversible: true,
              reversed: isReversedApplied('advertiserName'),
              data: withSelectedOptions('advertiser', advertiserNames, advertiserName),
              type: 'multiselect',
            },
            {
              title: t('fields.keyword'),
              field: 'keyword',
              reversed: isReversedApplied('keyword'),
              value: keyword,
              reversible: true,
            },
          ]}
          reverseCallBack={toggleReversedFilterAndApply}
        />
      </div>
      {notify && (
        <Notify className={'mt-[20px] -mb-[16px]'}>
          {
            {
              'demo-project': (
                <Trans i18nKey='other.demoProjectSelected'>
                  <NavLink className={clsx(styles.demoBannerLink)} to='/projects' />
                </Trans>
              ),
              'tracking-disabled': (
                <>
                  <Trans i18nKey='other.trackingDisabled'>
                    <NavLink
                      className={clsx(styles.demoBannerLink)}
                      to={project ? `/projects/${project}/affiliate-tracking` : '/projects'}
                    />
                  </Trans>
                </>
              ),
            }[notify]
          }
        </Notify>
      )}
      {hasPaidLinks && statuses && (
        <BpTabs
          selector
          active={
            statuses.find((option) => {
              if (Number(option.value) === status) return option
            })?.text
          }
          className={clsx(styles.tabsWrapper)}
          size={'md'}
          onToggle={(e) => {
            const status = Number(
              statuses.find((option) => {
                if (t('fields.options.' + option.text) === e) return option
              })?.value,
            )

            dispatch(setPpcComplianceStatus(status))
          }}
        >
          {statuses.map((status) => (
            <BpTab
              key={status.value}
              counter={
                status.text === 'Ignore' ? undefined : counter && counter[Number(status.value)]
              }
              panel={<PpcCompliancePanel />}
              title={t('fields.options.' + status.text)}
            />
          ))}
        </BpTabs>
      )}
    </section>
  )
}

export const PpcComplianceFilters = withSearchParams<
  IWithSearchParamsProps<IPpcComplianceFilter>,
  IPpcComplianceFilter
>(Filter, selectPpcComplianceFilter)
